import React from 'react';

import classNames from 'classnames';

import { detailMoney, money } from './contractDashboardHelpers';

import ChartLineIcon from 'Assets/icons/chart-line.svg';
import FileIcon from 'Assets/icons/file.svg';
import MinusIcon from 'Assets/icons/minus.svg';
import UserIcon from 'Assets/icons/user.svg';

/**
 * IB-703 — the five Contract Management KPI tiles. Mirrors the Overview KpiCard tokens
 * (tw-rounded-lg, default border-grey/white, blue variant for the hero figure). Per the mockup,
 * every tile except the hero carries a rounded icon badge next to its title.
 */
const KpiCard = ({ title, value, subtitle, variant = 'default', icon: Icon, iconClass }) => {
    const isColoured = variant === 'blue';

    return (
        <div
            className={classNames('tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-px-4 tw-py-4', {
                'tw-border-2 tw-border-grey tw-bg-white': variant === 'default',
                'tw-bg-blue tw-text-white': variant === 'blue',
            })}
        >
            <div className="tw-flex tw-items-center tw-gap-2">
                {Icon && (
                    <span className={classNames('tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg', iconClass)}>
                        <Icon className="tw-h-4 tw-w-4" />
                    </span>
                )}
                <div className={classNames('tw-text-sm tw-font-medium', isColoured ? 'tw-text-white tw-opacity-80' : 'tw-text-charcoal-75')}>{title}</div>
            </div>
            <div className={classNames('tw-text-2.5xl tw-font-semibold tw-leading-none', isColoured ? 'tw-text-white' : 'tw-text-charcoal')}>{value}</div>
            {subtitle && <div className={classNames('tw-text-xs', isColoured ? 'tw-text-white tw-opacity-80' : 'tw-text-charcoal-50')}>{subtitle}</div>}
        </div>
    );
};

const ContractKpiTiles = ({ kpis, currencySymbol }) => {
    const contractsCount = kpis?.contracts_count ?? 0;
    const contractsClosedCount = kpis?.contracts_closed_count ?? 0;
    const totalContractedValue = kpis?.total_contracted_value ?? 0;
    const avgContractValue = kpis?.avg_contract_value ?? 0;
    const actualsToDate = kpis?.actuals_to_date ?? 0;
    const actualsPct = kpis?.actuals_pct ?? 0;
    const varianceRemaining = kpis?.variance_remaining ?? 0;
    const suppliersCount = kpis?.suppliers_count ?? 0;

    return (
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-3">
            <KpiCard
                title={$t('Contracts')}
                value={contractsCount}
                subtitle={$t('{{count}} closed', { count: contractsClosedCount })}
                icon={FileIcon}
                iconClass="tw-bg-blue-15 tw-text-blue"
            />
            <KpiCard
                title={$t('Total contracted value')}
                value={money(currencySymbol, totalContractedValue)}
                subtitle={$t('avg {{value}} per contract', { value: detailMoney(currencySymbol, avgContractValue) })}
                variant="blue"
            />
            <KpiCard
                title={$t('Actuals to date')}
                value={money(currencySymbol, actualsToDate)}
                subtitle={$t('{{percent}}% of contracted', { percent: Math.round(actualsPct) })}
                icon={ChartLineIcon}
                iconClass="tw-bg-green-25 tw-text-green"
            />
            <KpiCard
                title={$t('Variance remaining')}
                value={money(currencySymbol, varianceRemaining)}
                subtitle={$t('committed, not yet spent')}
                icon={MinusIcon}
                iconClass="tw-bg-charcoal-15 tw-text-charcoal-75"
            />
            <KpiCard
                title={$t('Suppliers')}
                value={suppliersCount}
                subtitle={$t('under contract')}
                icon={UserIcon}
                iconClass="tw-bg-charcoal-15 tw-text-charcoal-75"
            />
        </div>
    );
};

export default ContractKpiTiles;
